import { FC } from 'react';
import { useTranslation } from '../../../../../utils/useTranslation';
import { IconChecked } from '../../../../icon/IconChecked';
import { IconLike } from '../../../../icon/IconLike';
import { IconPlus } from '../../../../icon/IconPlus';
import { Box } from '../../../../layout/Box';
import { DataTestId } from '../../../../layout/DataTestIdWrapper';
import { Flex } from '../../../../layout/Flex';
import { Stack } from '../../../../layout/Stack';
import { Typography } from '../../../../typography/Typography';
import { ListItem } from '../../styles';
import { CollectionListItem } from '../CollectionListItem/CollectionListItem';
import { CollectionListItemSkeleton } from '../CollectionListItemSkeleton/CollectionListItemSkeleton';
import { ExistentCollectionContentProps } from './types';

export const ExistentCollectionContent: FC<ExistentCollectionContentProps> = ({
    onAddClick,
    isCollectionsLoading,
    collections,
    onSaveClick,
    isFavoritesEnabled = false,
}) => {
    const t = useTranslation();

    const renderItem = (index: number) => {
        const collection = collections[index];

        if (!collection) {
            return <CollectionListItemSkeleton key={index} />;
        }

        return (
            <CollectionListItem
                key={collection.id}
                collection={collection}
                onClick={() => {
                    onSaveClick(collection.id);
                }}
            />
        );
    };

    return (
        <Flex direction="column" width="100%" height="100%">
            {isFavoritesEnabled ? (
                <Box width="100%" px={2} py={1}>
                    <Flex p={1} direction="row" spacing={2} alignItems="center" width="100%">
                        <Box
                            p={1.5}
                            borderRadius={1}
                            backgroundColor="var(--control-secondary-background)"
                        >
                            <IconLike
                                fontSize={24}
                                strokeWidth="2px"
                                stroke="var(--color-primary)"
                                fill="transparent"
                            />
                        </Box>
                        <Box>
                            <Typography variant="body" weight="medium">
                                {t('Моё')}
                            </Typography>
                            <Typography variant="caption" color="secondary">
                                {t('Ваше избранное на Яндексе')}
                            </Typography>
                        </Box>
                        <Box ml="auto">
                            <IconChecked fontSize={16} color="var(--color-primary)" />
                        </Box>
                    </Flex>
                </Box>
            ) : (
                <Box width="100%" px={2} py={1}>
                    <ListItem p={1} role="button" onClick={onAddClick}>
                        <Box
                            p={1.5}
                            borderRadius={1}
                            backgroundColor="var(--control-secondary-background)"
                        >
                            <IconPlus color="var(--color-primary)" fontSize={24} />
                        </Box>
                        <Typography variant="body" dataTestId={DataTestId.ADD_NEW_COLLECTION}>
                            {t('Добавить в новую коллекцию')}
                        </Typography>
                    </ListItem>
                </Box>
            )}

            {isFavoritesEnabled ? (
                <Box px={3} pt={2} pb={1}>
                    <Typography variant="body" weight="medium">
                        {t('Сохранить в коллекцию Ритма')}
                    </Typography>
                </Box>
            ) : null}

            {isFavoritesEnabled && !isCollectionsLoading && collections.length === 0 ? (
                <Flex
                    flex={1}
                    minHeight={{ sm: 200 }}
                    alignItems="center"
                    justifyContent="center"
                    px={3}
                >
                    <Typography variant="body" color="secondary" textAlign="center">
                        {t('Коллекциями можно делиться с друзьями и публиковать в ленте Ритма')}
                    </Typography>
                </Flex>
            ) : null}

            <Stack
                p={2}
                pt={0}
                overflowY="auto"
                count={isCollectionsLoading ? 5 : collections.length}
                width="100%"
                spacing={2}
                renderItem={renderItem}
            />
        </Flex>
    );
};
